// HSE fork: the CMMS Overview was a heavy 1200-line dashboard with
// three tabs, KPI cards, line/bar/pie charts, and a "my work" feed.
// None of those signals mean anything in HSE — the product is one
// verb (submit a risk report, acknowledge it) so this page collapses
// to a compact stat header + a live map of sites with active reports.
//
// Interactions on the map:
//   • Filter chip lets you narrow to acknowledged / unacknowledged
//     (all defaults on). "Acknowledged / unacknowledged" is
//     *per-user*, matching the Reports page — a report I've already
//     checked in is acknowledged for me even if teammates are still
//     pending.
//   • Click a site marker → a floating card lists every report at
//     that site with the running ack count. Reports I haven't
//     personally acknowledged expose an inline "Acknowledge" button
//     that fires the same endpoint the Reports page uses.

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box, Button, Card, Chip, CircularProgress, Divider, IconButton, Stack,
  ToggleButton, ToggleButtonGroup, Typography, useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Site } from '@cmms/types'
import { ApiError, type MaintenanceRequest } from '@cmms/api-client'

import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { useAuthStore } from '../../store/authStore'
import { toast } from '../../store/snackbarStore'
import { useOrgSettings } from '../../hooks/useOrgSettings'

type Filter = 'all' | 'unacknowledged' | 'acknowledged'

// Per-user effective status — mirrors the Reports page helper so the
// filter here means the same thing as the Reports tab of the same name.
function effectiveStatus(
  r: MaintenanceRequest,
  currentUserId: string | null,
): 'submitted' | 'acknowledged' {
  if (r.status === 'acknowledged') return 'acknowledged'
  if (!currentUserId) return 'submitted'
  const mine = r.acknowledgements.find((a) => a.user_id === currentUserId)
  return mine?.acknowledged_at ? 'acknowledged' : 'submitted'
}

export function OverviewPage() {
  const me   = useAuthStore((s) => s.user)
  const myId = me?.id ?? null
  const [filter, setFilter] = useState<Filter>('all')

  const sitesQ   = useQuery({ queryKey: ['sites'],    queryFn: () => api.sites.list()    })
  const reportsQ = useQuery({ queryKey: ['requests'], queryFn: () => api.requests.list() })

  const sites: Site[] = Array.isArray(sitesQ.data)   ? sitesQ.data   : []
  const reports: MaintenanceRequest[] = Array.isArray(reportsQ.data) ? reportsQ.data : []

  // Group visible reports by site id — driven by the filter chip so
  // markers appear/disappear as you toggle. Sites without matching
  // reports don't show up as pins at all; the whole "active site"
  // signal is derived from live report state.
  const visibleReportsBySite = useMemo(() => {
    const bySite = new Map<string, MaintenanceRequest[]>()
    for (const r of reports) {
      if (!r.site_id) continue
      const eff = effectiveStatus(r, myId)
      if (filter === 'acknowledged'   && eff !== 'acknowledged') continue
      if (filter === 'unacknowledged' && eff !== 'submitted')    continue
      const arr = bySite.get(r.site_id) ?? []
      arr.push(r)
      bySite.set(r.site_id, arr)
    }
    return bySite
  }, [reports, filter, myId])

  // Header stats. Kept intentionally minimal — this page is a map
  // first, dashboard second. Deeper analytics belong in Reports.
  const stats = useMemo(() => {
    const unacked = reports.filter((r) => effectiveStatus(r, myId) === 'submitted').length
    const acked   = reports.filter((r) => effectiveStatus(r, myId) === 'acknowledged').length
    return {
      sites:       sites.length,
      active:      visibleReportsBySite.size,
      reports:     reports.length,
      unacked,
      acked,
    }
  }, [sites, reports, visibleReportsBySite, myId])

  // Only sites with matching reports AND lat/lng can be pinned. Sites
  // without coords quietly drop from the map surface (they're still
  // visible from Sites & Reports).
  const pinSites: PinSite[] = useMemo(() => {
    const out: PinSite[] = []
    for (const s of sites) {
      const rs = visibleReportsBySite.get(s.id)
      if (!rs || rs.length === 0) continue
      if (s.latitude == null || s.longitude == null) continue
      out.push({ site: s, reports: rs })
    }
    return out
  }, [sites, visibleReportsBySite])

  const loading = sitesQ.isLoading || reportsQ.isLoading

  return (
    <PageShell
      title="Overview"
      subtitle="Live view of every site with active risk reports across your organisation."
      scrollContent={false}
    >
      {/* ── Compact stat strip ─────────────────────────────────── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <StatTile label="Sites"                icon={LocationOnOutlinedIcon} value={stats.sites} />
        <StatTile label="Active sites"         icon={LocationOnOutlinedIcon} value={stats.active} tone="brand" />
        <StatTile label="Reports (total)"      icon={DescriptionOutlinedIcon} value={stats.reports} />
        <StatTile label="Unacknowledged (you)" icon={ScheduleOutlinedIcon}    value={stats.unacked} tone="warn" />
        <StatTile label="Acknowledged (you)"   icon={CheckCircleOutlineIcon}  value={stats.acked} tone="ok" />
      </Stack>

      {/* ── Body — map on the left, analytics rail stacked on the
              right. On lg+ the map claims two-thirds of the width and
              the rail sits alongside so admins can compare pins to
              analytics without scrolling. Below lg the rail drops
              underneath the map and reverts to horizontal. */}
      <Box sx={{
        flex: 1, minHeight: 0,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
        gap: 2,
      }}>
        {/* ── Filter + map wrapper ──────────────────────────────── */}
        <Card sx={{ minHeight: 0, borderRadius: 3, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Stack direction="row" alignItems="center" spacing={1.5}
            sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Sites with active reports</Typography>
            <Chip label={pinSites.length} size="small"
              sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: 'action.hover' }} />
            <Box sx={{ flex: 1 }} />
            <ToggleButtonGroup value={filter} exclusive size="small"
              onChange={(_, v: Filter | null) => v && setFilter(v)}
              sx={{
                '& .MuiToggleButton-root': {
                  fontSize: 12, textTransform: 'none', px: 1.5, py: 0.5, borderRadius: 1.5,
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="unacknowledged">Unacknowledged</ToggleButton>
              <ToggleButton value="acknowledged">Acknowledged</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            {loading ? (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={26} />
              </Box>
            ) : (
              <SiteReportsMap pinSites={pinSites} currentUserId={myId} />
            )}
          </Box>
        </Card>

        {/* ── Analytics rail — stacked cards on the right of the map.
                Column on lg+, horizontal row on smaller screens.
                Sizing on lg+ (column mode):
                  • Category breakdown: fixed height — the donut fills
                    naturally at ~210px, more is wasted whitespace.
                  • Activity trend:     fixed height — the sparkline +
                    hero number reads well at ~180px.
                  • Sites needing attention: flex: 1, so it soaks up
                    all the remaining rail height and scrolls
                    internally when the list exceeds the visible box.
                On xs–md the horizontal row keeps each card at 240px
                so the phone view isn't a huge vertical strip. */}
        <Stack
          direction={{ xs: 'column', md: 'row', lg: 'column' }}
          spacing={2}
          sx={{ minHeight: 0 }}
        >
          <Box sx={{
            flex: { xs: 'none', md: 1, lg: '0 0 210px' },
            minHeight: { xs: 220, md: 0 },
            display: 'flex', flexDirection: 'column', minWidth: 0,
          }}>
            <CategoryBreakdown reports={reports} />
          </Box>
          <Box sx={{
            flex: { xs: 'none', md: 1, lg: '0 0 180px' },
            minHeight: { xs: 220, md: 0 },
            display: 'flex', flexDirection: 'column', minWidth: 0,
          }}>
            <ActivityTrend reports={reports} />
          </Box>
          <Box sx={{
            flex: { xs: 'none', md: 1, lg: '0 0 220px' },
            minHeight: { xs: 220, md: 0 },
            display: 'flex', flexDirection: 'column', minWidth: 0,
          }}>
            <TopReporters reports={reports} />
          </Box>
          <Box sx={{
            flex: { xs: 'none', md: 1, lg: '1 1 0' },
            minHeight: { xs: 220, md: 0 },
            display: 'flex', flexDirection: 'column', minWidth: 0,
          }}>
            <TopSites reports={reports} sites={sites} />
          </Box>
        </Stack>
      </Box>
    </PageShell>
  )
}

// ────────────────────────────────────────────────────────────────
// Header stat tile
// ────────────────────────────────────────────────────────────────

function StatTile({
  label, value, icon: Icon, tone = 'muted',
}: {
  label: string
  value: number
  icon: React.ElementType
  tone?: 'muted' | 'brand' | 'warn' | 'ok'
}) {
  // Palette holds a base hex per tone. Light + dark variants of the
  // card are derived from that base so both palettes stay coherent
  // without a second lookup table.
  const base =
    tone === 'brand' ? '#4338CA'
    : tone === 'warn'  ? '#B45309'
    : tone === 'ok'    ? '#059669'
    :                    '#475569'

  return (
    <Card sx={{
      flex: 1, minWidth: 0, px: 2, py: 1.5, borderRadius: 2.5,
      border: '1px solid',
      // Dark mode: subtle tinted surface with a matching border glow so
      // the tone still reads without shouting. Light mode: pastel wash
      // with the ring border.
      borderColor: (t) => t.palette.mode === 'light' ? `${base}33` : `${base}55`,
      bgcolor:     (t) => t.palette.mode === 'light' ? `${base}10` : `${base}18`,
      boxShadow: 'none',
      display: 'flex', alignItems: 'center', gap: 1.5,
    }}>
      <Box sx={{
        width: 34, height: 34, borderRadius: 1.5, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        bgcolor: (t) => t.palette.mode === 'light' ? '#FFFFFFCC' : `${base}22`,
        border: '1px solid',
        borderColor: (t) => t.palette.mode === 'light' ? `${base}33` : `${base}44`,
      }}>
        <Icon sx={{ fontSize: 18, color: base }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontSize: 20, fontWeight: 800, lineHeight: 1.1,
          color: (t) => t.palette.mode === 'light' ? base : `${base}`,
        }}>
          {value.toLocaleString()}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
    </Card>
  )
}

// ────────────────────────────────────────────────────────────────
// Dashboard analytics — category / trend / top sites
// ────────────────────────────────────────────────────────────────

// Local mapping of the 14 HSE risk-category keys to their display
// labels + a colour that pops. Kept inline (rather than fetched)
// because the catalogue is stable and every request row already
// carries the key. If the backend ever grows the catalogue we serve
// the extra entries under a neutral fallback.
const CATEGORY_META: Record<string, { label: string; color: string }> = {
  fire_safety:         { label: 'Fire safety',         color: '#DC2626' },
  electrical_safety:   { label: 'Electrical safety',   color: '#EA580C' },
  structural:          { label: 'Structural',          color: '#B45309' },
  hvac:                { label: 'HVAC',                color: '#0D9488' },
  chemical_safety:     { label: 'Chemical safety',     color: '#7C3AED' },
  security:            { label: 'Security',            color: '#1D4ED8' },
  medical:             { label: 'Medical',             color: '#DB2777' },
  working_at_heights:  { label: 'Working at heights',  color: '#9333EA' },
  confined_space:      { label: 'Confined space',      color: '#6D28D9' },
  natural_disaster:    { label: 'Natural disaster',    color: '#0F766E' },
  contractor_safety:   { label: 'Contractor safety',   color: '#0891B2' },
  occupational_health: { label: 'Occupational health', color: '#059669' },
  water_safety:        { label: 'Water safety',        color: '#0284C7' },
  waste_management:    { label: 'Waste management',    color: '#65A30D' },
}

function DashboardCard({
  title, action, accent = '#3B82F6', children,
}: {
  title: string
  action?: React.ReactNode
  /** Card-top gradient accent + subtle glow. Defaults to primary blue. */
  accent?: string
  children: React.ReactNode
}) {
  return (
    <Card sx={{
      flex: 1, minWidth: 0, borderRadius: 2.5, border: '1px solid',
      borderColor: 'divider',
      display: 'flex', flexDirection: 'column',
      position: 'relative', overflow: 'hidden',
      boxShadow: (t) => t.palette.mode === 'light'
        ? `0 10px 24px -14px ${accent}55, 0 2px 6px rgba(15,23,42,0.06)`
        : '0 6px 18px -10px rgba(0,0,0,0.6)',
      transition: 'transform 200ms ease, box-shadow 200ms ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: (t) => t.palette.mode === 'light'
          ? `0 16px 32px -18px ${accent}80, 0 4px 10px rgba(15,23,42,0.08)`
          : '0 10px 26px -12px rgba(0,0,0,0.7)',
      },
    }}>
      {/* Accent bar across the top */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${accent} 0%, ${accent}00 100%)`,
      }} />
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{ px: 2, pt: 1.75, pb: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: 'text.secondary', flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      <Box sx={{ px: 2, pb: 1.75, flex: 1, minWidth: 0 }}>{children}</Box>
    </Card>
  )
}

/** Reports by risk category — vertical list of categories with count
 *  chips and a proportional fill bar. Compared to the old donut this
 *  reads clean at narrow rail widths and every category is visible
 *  without a legend. Categories with zero reports are hidden so the
 *  list stays tight; a footer chip shows the total. */
function CategoryBreakdown({ reports }: { reports: MaintenanceRequest[] }) {
  const { rows, total, unclassified } = useMemo(() => {
    const counts = new Map<string, number>()
    let unc = 0
    for (const r of reports) {
      const k = r.risk_category
      if (!k || !CATEGORY_META[k]) {
        unc += 1
        continue
      }
      counts.set(k, (counts.get(k) ?? 0) + 1)
    }
    const tot = reports.length
    const arr = Array.from(counts.entries())
      .map(([k, n]) => ({
        key:    k,
        label:  CATEGORY_META[k]!.label,
        color:  CATEGORY_META[k]!.color,
        count:  n,
        pct:    tot > 0 ? (n / tot) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count)
    return { rows: arr, total: tot, unclassified: unc }
  }, [reports])

  const topCategory = rows[0]

  return (
    <DashboardCard
      title="Reports by risk category"
      accent={topCategory?.color ?? '#3B82F6'}
      action={total > 0 && (
        <Chip label={`${total} total`} size="small" sx={{
          height: 20, fontSize: 10.5, fontWeight: 800,
          bgcolor: (topCategory?.color ?? '#3B82F6') + '18',
          color: topCategory?.color ?? '#3B82F6',
        }} />
      )}
    >
      {total === 0 ? (
        <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 3, textAlign: 'center' }}>
          No reports yet.
        </Typography>
      ) : rows.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 3, textAlign: 'center' }}>
          {unclassified} report{unclassified === 1 ? '' : 's'} without a category. Reports filed via the public QR flow are tagged automatically.
        </Typography>
      ) : (
        <Box sx={{
          // Internal scroll — 14 possible categories don't always fit
          // the pinned card height. Thin scrollbar keeps the design clean.
          height: '100%', overflowY: 'auto', pr: 0.5,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar':       { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'action.hover', borderRadius: 99 },
        }}>
          <Stack spacing={1}>
            {rows.map((r) => (
              <Box key={r.key}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.4 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: r.color, flexShrink: 0,
                    boxShadow: `0 0 0 3px ${r.color}22` }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: r.color }}>
                    {r.count}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.disabled',
                    width: 34, textAlign: 'right' }}>
                    {r.pct.toFixed(0)}%
                  </Typography>
                </Stack>
                <Box sx={{ height: 3, borderRadius: 99, bgcolor: 'action.hover' }}>
                  <Box sx={{
                    width: `${r.pct}%`, height: '100%', borderRadius: 99,
                    bgcolor: r.color,
                    transition: 'width 240ms ease',
                  }} />
                </Box>
              </Box>
            ))}
            {unclassified > 0 && (
              <Typography sx={{ fontSize: 10.5, color: 'text.disabled', fontWeight: 600,
                pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                + {unclassified} uncategorised
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </DashboardCard>
  )
}

/** 14-day activity with a smooth gradient area chart. Hero shows
 *  "last 7 days" total + a coloured delta pill; the chart plots the
 *  daily series so admins can eyeball spikes and lulls. */
function ActivityTrend({ reports }: { reports: MaintenanceRequest[] }) {
  const days = 14
  const perDay = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const arr = new Array(days).fill(0)
    for (const r of reports) {
      const d = new Date(r.created_at)
      d.setHours(0, 0, 0, 0)
      const idx = days - 1 - Math.floor((today.getTime() - d.getTime()) / 86_400_000)
      if (idx >= 0 && idx < days) arr[idx]!++
    }
    return arr
  }, [reports])

  const max = Math.max(1, ...perDay)
  const total7d  = perDay.slice(-7).reduce((a, b) => a + b, 0)
  const totalPre = perDay.slice(0, -7).reduce((a, b) => a + b, 0)
  const delta = total7d - totalPre
  const rising = delta > 0
  const falling = delta < 0
  const trendColor = rising ? '#DC2626' : falling ? '#059669' : '#64748B'
  const trendArrow = rising ? '↑' : falling ? '↓' : '→'
  const deltaTxt = delta === 0 ? 'flat' : `${rising ? '+' : ''}${delta}`

  // SVG coordinate math — plot 14 daily points across a 200×80 viewbox.
  const w = 200, h = 80, padY = 6
  const stepX = w / (days - 1)
  const yFor = (n: number) => padY + (1 - n / max) * (h - padY * 2)
  const points = perDay.map((n, i) => [i * stepX, yFor(n)] as const)
  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`
  const [lastX, lastY] = points[points.length - 1]!

  return (
    <DashboardCard
      title="Activity (14 days)"
      accent={trendColor}
      action={
        <Chip label={`${trendArrow} ${deltaTxt} vs prior 7d`} size="small"
          sx={{ height: 20, fontSize: 10.5, fontWeight: 800,
            bgcolor: trendColor + '18', color: trendColor }} />
      }
    >
      <Stack direction="row" spacing={2} alignItems="stretch">
        {/* Hero number */}
        <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column',
            justifyContent: 'center', pr: 1.5, borderRight: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 28, fontWeight: 900, letterSpacing: '-0.02em',
            lineHeight: 1, color: trendColor }}>
            {total7d}
          </Typography>
          <Typography sx={{ fontSize: 10, color: 'text.disabled', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.5 }}>
            last 7 days
          </Typography>
        </Box>

        {/* Smooth gradient area chart */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ width: '100%', height: 80 }}>
            <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="100%"
              preserveAspectRatio="none" style={{ display: 'block' }}>
              <defs>
                <linearGradient id="activityFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={trendColor} stopOpacity="0.45" />
                  <stop offset="100%" stopColor={trendColor} stopOpacity="0.02" />
                </linearGradient>
              </defs>
              <path d={areaPath} fill="url(#activityFill)" />
              <path d={linePath} fill="none" stroke={trendColor} strokeWidth={2}
                strokeLinecap="round" strokeLinejoin="round" />
              {/* Today marker */}
              <circle cx={lastX} cy={lastY} r={3.5} fill="#FFFFFF"
                stroke={trendColor} strokeWidth={2} />
            </svg>
          </Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
            <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>14d ago</Typography>
            <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>Today</Typography>
          </Stack>
        </Box>
      </Stack>
    </DashboardCard>
  )
}

/** Sites needing the most attention — ordered by report count with
 *  a proportional fill bar. The parent Stack pins the row's height,
 *  so this card scrolls internally when the list overflows rather
 *  than pushing the map down. Trailing "+N more" footer flags the
 *  scroll affordance when it kicks in. */

/** Top reporters — who's filing risk reports and how many each. Works
 *  for both internal reporters (submitted_by_name via the app) and
 *  public QR reporters (submitter_name via the /report/... form).
 *  Same visual language as TopSites so the rail reads consistently:
 *  numeric rank, name / email, count chip, proportional fill bar. */
function TopReporters({ reports }: { reports: MaintenanceRequest[] }) {
  const rows = useMemo(() => {
    // Group by whichever identifier is present. Internal reporters
    // prefer the display name; public QR reporters fall back to the
    // email so two "Anonymous" entries with different emails don't
    // collapse into one row.
    type Row = { key: string; name: string; email: string | null; count: number; internal: boolean }
    const grouped = new Map<string, Row>()
    for (const r of reports) {
      const internal = !!r.submitted_by_name
      const name = r.submitted_by_name
        ?? r.submitter_name
        ?? r.requester_name
        ?? null
      const email = r.submitted_by_email
        ?? r.submitter_email
        ?? r.requester_email
        ?? null
      if (!name && !email) continue // truly anonymous — skip
      const key = internal
        ? `u:${r.submitted_by ?? name ?? email}`
        : `p:${email ?? name}`
      const existing = grouped.get(key)
      if (existing) {
        existing.count += 1
      } else {
        grouped.set(key, {
          key,
          name: name ?? email ?? 'Unknown reporter',
          email,
          count: 1,
          internal,
        })
      }
    }
    const arr = Array.from(grouped.values())
    const max = Math.max(1, ...arr.map((r) => r.count))
    return arr
      .sort((a, b) => b.count - a.count)
      .map((r) => ({ ...r, pct: (r.count / max) * 100 }))
  }, [reports])

  return (
    <DashboardCard title="Top reporters" accent="#4338CA">
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 3, textAlign: 'center' }}>
          No reporters yet.
        </Typography>
      ) : (
        <Box sx={{
          height: '100%', overflowY: 'auto', pr: 0.5,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar':       { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'action.hover', borderRadius: 99 },
        }}>
          <Stack spacing={1.5}>
            {rows.map((r, i) => (
              <Box key={r.key}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Box sx={{ width: 22, height: 22, borderRadius: 99,
                      bgcolor: 'action.hover', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary' }}>
                      {i + 1}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.name}
                      </Typography>
                      {!r.internal && (
                        <Chip label="public" size="small" sx={{
                          height: 14, fontSize: 8.5, fontWeight: 700,
                          bgcolor: '#F59E0B18', color: '#B45309',
                          '& .MuiChip-label': { px: 0.6 },
                        }} />
                      )}
                    </Stack>
                    {r.email && r.email !== r.name && (
                      <Typography sx={{ fontSize: 10.5, color: 'text.disabled',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.email}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={`${r.count} ${r.count === 1 ? 'report' : 'reports'}`} size="small"
                    sx={{ height: 20, fontSize: 10.5, fontWeight: 800,
                      bgcolor: '#4338CA18', color: '#4338CA' }} />
                </Stack>
                <Box sx={{ height: 4, borderRadius: 99, bgcolor: 'action.hover', ml: 3.75 }}>
                  <Box sx={{
                    width: `${r.pct}%`, height: '100%', borderRadius: 99,
                    background: 'linear-gradient(90deg, #4338CA 0%, #818CF8 100%)',
                    transition: 'width 240ms ease',
                  }} />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </DashboardCard>
  )
}

function TopSites({ reports, sites }: { reports: MaintenanceRequest[]; sites: Site[] }) {
  const { rows, totalSitesWithReports } = useMemo(() => {
    const bySite = new Map<string, number>()
    for (const r of reports) {
      if (!r.site_id) continue
      bySite.set(r.site_id, (bySite.get(r.site_id) ?? 0) + 1)
    }
    const nameById = new Map(sites.map((s) => [s.id, s.name]))
    const cityById = new Map(sites.map((s) => [s.id, s.address]))
    const max = Math.max(1, ...bySite.values())
    const all = Array.from(bySite.entries())
      .map(([id, count]) => ({
        id, count, pct: (count / max) * 100,
        name: nameById.get(id) ?? 'Unknown site',
        address: cityById.get(id) ?? null,
      }))
      .sort((a, b) => b.count - a.count)
    return { rows: all, totalSitesWithReports: all.length }
  }, [reports, sites])

  const VISIBLE_HINT = 4

  return (
    <DashboardCard title="Sites needing attention" accent="#B45309">
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 3, textAlign: 'center' }}>
          No site reports yet.
        </Typography>
      ) : (
        <Box sx={{
          // Internal scroll — the parent Stack pins this row's height,
          // so any overflow lives inside the card instead of pushing
          // the map. Thin scrollbar so the list still reads clean.
          height: '100%', overflowY: 'auto', pr: 0.5,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar':       { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'action.hover', borderRadius: 99 },
        }}>
          <Stack spacing={1.5}>
            {rows.map((r, i) => (
              <Box key={r.id}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Box sx={{ width: 22, height: 22, borderRadius: 99,
                      bgcolor: 'action.hover', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary' }}>
                      {i + 1}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.name}
                    </Typography>
                    {r.address && (
                      <Typography sx={{ fontSize: 10.5, color: 'text.disabled',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.address}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={`${r.count} ${r.count === 1 ? 'report' : 'reports'}`} size="small"
                    sx={{ height: 20, fontSize: 10.5, fontWeight: 800,
                      bgcolor: '#B4530918', color: '#B45309' }} />
                </Stack>
                <Box sx={{ height: 4, borderRadius: 99, bgcolor: 'action.hover', ml: 3.75 }}>
                  <Box sx={{
                    width: `${r.pct}%`, height: '100%', borderRadius: 99,
                    background: 'linear-gradient(90deg, #B45309 0%, #F59E0B 100%)',
                    transition: 'width 240ms ease',
                  }} />
                </Box>
              </Box>
            ))}
          </Stack>
          {totalSitesWithReports > VISIBLE_HINT && (
            <Typography sx={{
              fontSize: 10.5, color: 'text.disabled', textAlign: 'center',
              mt: 1.25, pt: 1, borderTop: '1px dashed', borderColor: 'divider',
            }}>
              Scroll for {totalSitesWithReports - VISIBLE_HINT} more site{totalSitesWithReports - VISIBLE_HINT === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
      )}
    </DashboardCard>
  )
}

// ────────────────────────────────────────────────────────────────
// Map + click-through detail card
// ────────────────────────────────────────────────────────────────

interface PinSite {
  site: Site
  reports: MaintenanceRequest[]
}

// Red teardrop pin with the report count inside. The outer white ring
// lifts the pin off the pastel CARTO basemap; the count carries the
// signal ("how many active reports at this site"). Ack state is still
// available via the hover tooltip + click-through card, but the pin
// itself stays a consistent red so a busy incident hotspot always
// reads the same way regardless of how many teammates have checked in.
function pinIcon(count: number, _hasUnacked: boolean): L.DivIcon {
  // Wider pill for 3+ digit counts so the number never truncates.
  const width = count >= 100 ? 42 : count >= 10 ? 38 : 34
  return L.divIcon({
    className: 'hse-site-pin',
    html: `
      <div style="
        width: ${width}px; height: 34px; border-radius: 999px;
        background: #FFFFFF; padding: 3px; box-sizing: border-box;
        box-shadow: 0 4px 10px rgba(15,23,42,0.25), 0 1px 3px rgba(15,23,42,0.15);
      ">
        <div style="
          width: 100%; height: 100%; border-radius: 999px;
          background: #DC2626;
          display: flex; align-items: center; justify-content: center;
          color: #FFFFFF;
          font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
          font-size: 13px; font-weight: 800; letter-spacing: -0.2px;
        ">${count}</div>
      </div>
    `,
    iconSize: [width, 34],
    iconAnchor: [width / 2, 17],
  })
}

function SiteReportsMap({
  pinSites, currentUserId,
}: {
  pinSites: PinSite[]
  currentUserId: string | null
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef       = useRef<L.Map | null>(null)
  const tileLayerRef = useRef<L.TileLayer | null>(null)
  const markersRef   = useRef<L.Marker[]>([])
  const [selected, setSelected] = useState<PinSite | null>(null)
  const qc = useQueryClient()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const ackM = useMutation({
    mutationFn: (id: string) => api.requests.acknowledge(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['requests'] })
      toast.success('Report acknowledged')
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : "Couldn't acknowledge"),
  })

  // One-shot init. The tile layer itself gets swapped in a separate
  // effect on theme changes.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = L.map(containerRef.current, {
      center: [20, 0], zoom: 2,
      zoomControl: false, attributionControl: false,
    })
    L.control.zoom({ position: 'topleft' }).addTo(map)
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      tileLayerRef.current = null
    }
  }, [])

  // Reactive tile-layer swap. Voyager (cream / pastel roads) for
  // light mode, Dark Matter (near-black / muted labels) for dark
  // mode. Both are CARTO raster styles and both support retina via
  // the {r} placeholder. On mode flip we swap in-place instead of
  // rebuilding the whole map so state (viewport, markers, popups) is
  // preserved.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (tileLayerRef.current) {
      tileLayerRef.current.remove()
      tileLayerRef.current = null
    }
    const url = isDark
      ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
      : 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'
    tileLayerRef.current = L.tileLayer(url, {
      subdomains: ['a', 'b', 'c', 'd'],
      maxZoom: 20,
    }).addTo(map)
  }, [isDark])

  // Reactive marker sync — re-renders whenever `pinSites` changes.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    // Wipe previous markers so a filter change doesn't leave orphans.
    markersRef.current.forEach((m) => m.remove())
    markersRef.current = []

    // If the currently-selected pin is no longer visible (filter
    // change), close the card so the UI stays consistent.
    if (selected && !pinSites.find((p) => p.site.id === selected.site.id)) {
      setSelected(null)
    }

    if (pinSites.length === 0) return

    const bounds: L.LatLngExpression[] = []
    for (const p of pinSites) {
      const hasUnacked = p.reports.some(
        (r) => effectiveStatusReport(r, currentUserId) === 'submitted',
      )
      const marker = L.marker(
        [p.site.latitude as number, p.site.longitude as number],
        { icon: pinIcon(p.reports.length, hasUnacked) },
      )
      marker.on('click', () => setSelected(p))
      marker.bindTooltip(
        `<strong>${escapeHtml(p.site.name)}</strong><br />${p.reports.length} report${p.reports.length === 1 ? '' : 's'}`,
        { direction: 'top', offset: [0, -12] },
      )
      marker.addTo(map)
      markersRef.current.push(marker)
      bounds.push([p.site.latitude as number, p.site.longitude as number])
    }

    // Fit-to-content on filter change so admins immediately see the
    // matching set. `padding` keeps pins away from the UI overlays.
    if (bounds.length === 1) {
      map.setView(bounds[0] as L.LatLngExpression, 12)
    } else if (bounds.length > 1) {
      map.fitBounds(bounds as L.LatLngBoundsLiteral, { padding: [40, 40], maxZoom: 12 })
    }
  }, [pinSites, currentUserId, selected])

  return (
    <>
      <Box ref={containerRef} sx={{ position: 'absolute', inset: 0, zIndex: 0 }} />
      {pinSites.length === 0 && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Card sx={{ px: 3, py: 2, borderRadius: 2, textAlign: 'center',
              maxWidth: 320, boxShadow: (t) => t.palette.mode === 'light'
                ? '0 8px 24px -6px rgba(15,23,42,0.14)'
                : '0 8px 24px -6px rgba(0,0,0,0.4)' }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 26, color: 'text.disabled', mb: 0.5 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.25 }}>
              No sites match this filter
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Try switching the filter above.
            </Typography>
          </Card>
        </Box>
      )}
      {selected && (
        <SiteReportsCard
          pin={selected}
          currentUserId={currentUserId}
          acknowledging={ackM.isPending}
          onAcknowledge={(id) => ackM.mutate(id)}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  )
}

// ────────────────────────────────────────────────────────────────
// Floating detail card
// ────────────────────────────────────────────────────────────────

function SiteReportsCard({
  pin, currentUserId, acknowledging, onAcknowledge, onClose,
}: {
  pin: PinSite
  currentUserId: string | null
  acknowledging: boolean
  onAcknowledge: (id: string) => void
  onClose: () => void
}) {
  const { formatDate } = useOrgSettings()
  const total = pin.reports.length
  const ackedForMe = pin.reports.filter(
    (r) => effectiveStatusReport(r, currentUserId) === 'acknowledged',
  ).length

  return (
    <Card sx={{
      position: 'absolute', top: 16, right: 16, width: 360, maxHeight: 'calc(100% - 32px)',
      overflowY: 'auto', zIndex: 1000, borderRadius: 2.5,
      boxShadow: (t) => t.palette.mode === 'light'
        ? '0 16px 40px -12px rgba(15,23,42,0.28)'
        : '0 16px 40px -12px rgba(0,0,0,0.6)',
    }}>
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <LocationOnOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pin.site.name}
          </Typography>
          {pin.site.address && (
            <Typography sx={{ fontSize: 11, color: 'text.secondary',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {pin.site.address}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ mr: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
            label={`${ackedForMe} of ${total} acknowledged by you`}
            size="small"
            sx={{ fontSize: 11, fontWeight: 700,
              bgcolor: ackedForMe === total ? '#DCFCE7' : '#FEF3C7',
              color:   ackedForMe === total ? '#166534' : '#92400E' }} />
        </Stack>
      </Box>

      <Stack divider={<Divider />} spacing={0}>
        {pin.reports.map((r) => (
          <ReportRow
            key={r.id}
            report={r}
            currentUserId={currentUserId}
            acknowledging={acknowledging}
            onAcknowledge={onAcknowledge}
            formatDate={formatDate}
          />
        ))}
      </Stack>
    </Card>
  )
}

function ReportRow({
  report: r, currentUserId, acknowledging, onAcknowledge, formatDate,
}: {
  report: MaintenanceRequest
  currentUserId: string | null
  acknowledging: boolean
  onAcknowledge: (id: string) => void
  formatDate: (d: string) => string
}) {
  const mine = currentUserId
    ? r.acknowledgements.find((a) => a.user_id === currentUserId)
    : null
  const iAcked = r.status === 'acknowledged' || Boolean(mine?.acknowledged_at)
  const iAmInRoster = !!mine
  const fullyAcked = r.status === 'acknowledged'

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.25,
        overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
        {r.title}
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          {formatDate(r.created_at)}
        </Typography>
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>·</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PeopleAltOutlinedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {r.ack_count} of {r.ack_total} acknowledged
          </Typography>
        </Stack>
      </Stack>

      {iAcked ? (
        <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
          label={fullyAcked ? 'Fully acknowledged' : 'You acknowledged'}
          size="small"
          sx={{ fontSize: 11, fontWeight: 700, height: 22,
            bgcolor: '#DCFCE7', color: '#166534' }} />
      ) : iAmInRoster || r.ack_total === 0 ? (
        <Button
          variant="contained" color="success" size="small"
          disabled={acknowledging}
          startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
          onClick={() => onAcknowledge(r.id)}
          sx={{ fontSize: 12, fontWeight: 700, textTransform: 'none', px: 1.5, py: 0.5 }}
        >
          {acknowledging ? 'Acknowledging…' : 'Acknowledge'}
        </Button>
      ) : (
        <Chip label="Not on this report's team" size="small"
          sx={{ fontSize: 11, height: 20, bgcolor: 'action.hover', color: 'text.secondary' }} />
      )}
    </Box>
  )
}

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

/** Re-exported effectiveStatus so the marker layer and card can share. */
function effectiveStatusReport(
  r: MaintenanceRequest,
  currentUserId: string | null,
): 'submitted' | 'acknowledged' {
  return effectiveStatus(r, currentUserId)
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
